import type { FunctionComponent } from "react";
import { Chart } from "react-chartjs-2";

import { REVENUE_SOURCE_COLORS } from "~/modules/reports/components/GlobalReportsView/components/RevenueReportSection/revenueSourceColors.ts";
import type {
	YearlyRevenueChartData,
} from "~/modules/reports/types/revenueReportViewTypes.ts";
import { formatCentsToCurrency } from "~/utils/currencyUtils.ts";

type Props = {
	data: YearlyRevenueChartData[];
	showTimeTracking: boolean;
	showInvoiced: boolean;
};

const YearlyRevenueByProjectChart: FunctionComponent<Props> = ({ data, showTimeTracking, showInvoiced }) => {
	// Sort by the visible source, so the biggest bars stay on top. When both sources
	// show, sort by time tracking; when only invoiced shows, sort by invoiced.
	const sortValue = (item: YearlyRevenueChartData) =>
		showInvoiced && !showTimeTracking ? item.invoiced : item.timeTracking;
	const sortedData = [...data].sort((a, b) => sortValue(b) - sortValue(a));
	const labels = sortedData.map((item) => item.label);

	// Normalize the axis to the highest value across both sources, so toggling a
	// source on or off never rescales the chart.
	const axisMax = data.reduce((max, item) => Math.max(max, item.timeTracking, item.invoiced), 0);

	const datasets = [
		showTimeTracking && {
			label: "Aufwand",
			data: sortedData.map((item) => item.timeTracking),
			backgroundColor: REVENUE_SOURCE_COLORS.timeTracking,
			borderColor: REVENUE_SOURCE_COLORS.timeTracking,
			borderWidth: 0,
		},
		showInvoiced && {
			label: "Abgerechnet",
			data: sortedData.map((item) => item.invoiced),
			backgroundColor: REVENUE_SOURCE_COLORS.invoiced,
			borderColor: REVENUE_SOURCE_COLORS.invoiced,
			borderWidth: 0,
		},
	].filter(Boolean);

	const chartData = {
		labels,
		datasets,
	};

	const options = {
		indexAxis: 'y' as const,
		responsive: true,
		maintainAspectRatio: false,
		animation: false,
		// Keep room to the right so a bar's value label is never clipped.
		layout: {
			padding: { right: 24 },
		},
		scales: {
			x: {
				beginAtZero: true,
				// Headroom above the largest bar for its value label.
				max: axisMax * 1.15,
				title: {
					display: false,
					text: "Average Daily Rate (€)",
				},
				ticks: {
					callback: (value: number) => `${formatCentsToCurrency(value)}`,
				},
			},
			y: {
				offset: true,
				ticks: {
					autoSkip: false,
					maxRotation: 0,
					minRotation: 0,
				},
			},
		},
		plugins: {
			legend: {
				display: false,
			},
			tooltip: {
				callbacks: {
					label: (context: any) => `${formatCentsToCurrency(context.parsed.x)}`,
				},
			},
			datalabels: {
				color: 'black',
				font: {
					weight: 'bold'
				},
				anchor: 'end',
				align: 'right',
				clamp:true,
				formatter: (value: number) => `${formatCentsToCurrency(value)}`,
			},
		},
	};

	const barsPerCategory = (showTimeTracking ? 1 : 0) + (showInvoiced ? 1 : 0);
	const rowHeight = barsPerCategory >= 2 ? 56 : 40;

	return (
		<div style={{ height: `${Math.max(400, data.length * rowHeight)}px`, width: "100%" }}>
			{/*@ts-expect-error library type definition is wrong*/}
			<Chart type="bar" data={chartData} options={options} />
		</div>
	);
};

export default YearlyRevenueByProjectChart;
